import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = 'https://api.notely.se';

type Platform = 'ios' | 'android' | 'macos' | 'other';

function isUrl(code: string): boolean {
  return code.startsWith('http://') || code.startsWith('https://');
}

function buildFallbackUrl(code: string, platform: Platform): string {
  if (platform === 'android') return `https://play.google.com/redeem?code=${encodeURIComponent(code)}`;
  if (platform === 'macos') return `https://apps.apple.com/app/notely-ai/id6740462619?platform=mac`;
  return isUrl(code) ? code : `/code/${code}`;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/.test(ua) && !('ontouchend' in document)) return 'macos';
  return 'other';
}

const CampaignRedirect: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const didRun = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [claimError, setClaimError] = useState(false);

  useEffect(() => {
    if (!campaignId || didRun.current) return;
    didRun.current = true;

    const platform = detectPlatform();
    const storageKey = `notely_campaign_${campaignId}`;

    const run = async () => {
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        try {
          const res = await fetch(
            `${API_BASE}/c/${campaignId}/verify?code=${encodeURIComponent(stored)}&platform=${platform}`
          );
          if (res.ok) {
            const data = await res.json();
            if (data.valid) {
              window.location.replace(data.redirect_url ?? buildFallbackUrl(stored, platform));
              return;
            }
          }
          // Code no longer valid — clear and fall through to claim
          localStorage.removeItem(storageKey);
        } catch {
          // Network error — use stored code optimistically
          window.location.replace(buildFallbackUrl(stored, platform));
          return;
        }
      }

      // Claim a new code
      try {
        const res = await fetch(`${API_BASE}/c/${campaignId}/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ platform }),
        });
        if (!res.ok) {
          setIsLoading(false);
          setClaimError(true);
          return;
        }
        const data = await res.json();
        if (data.code) {
          localStorage.setItem(storageKey, data.code);
          window.location.replace(data.redirect_url ?? buildFallbackUrl(data.code, platform));
        } else if (data.fallback_url) {
          window.location.replace(data.fallback_url);
        } else {
          setIsLoading(false);
          setClaimError(true);
        }
      } catch {
        setIsLoading(false);
        setClaimError(true);
      }
    };

    run();
  }, [campaignId]);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="text-center">
        {claimError ? (
          <p className="text-red-400">Something went wrong. Please try again later.</p>
        ) : (
          <>
            <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Getting your promo code…</p>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignRedirect;
